import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, Envelope, Phone } from "@phosphor-icons/react";

export default function SmsSignup() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!consent) {
      setErr("Please check the consent box to continue.");
      return;
    }
    setSending(true);
    try {
      await api.post("/sms-signups", { name, phone, consent, source_url: window.location.href });
      setDone(true);
    } catch (er) {
      setErr(er.response?.data?.detail?.toString() || er.message);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center">
          <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto" />
          <h1 className="text-2xl font-black mt-4">You're signed up</h1>
          <p className="text-sm text-slate-600 mt-2">
            Thanks, {name || "there"}. You'll receive a text at{" "}
            <span className="font-mono font-bold">{phone}</span> confirming
            your subscription. From here on, we'll text you when a contractor
            submits a timesheet for your approval.
          </p>
          <p className="text-xs text-slate-500 mt-4">
            To unsubscribe at any time, reply <b>STOP</b> to any message we
            send. For help, reply <b>HELP</b>.
          </p>
          <Link to="/" className="mt-6 inline-block text-sm text-green-700 hover:underline">
            Return to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-xl mx-auto p-6 md:p-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">← Home</Link>

        <div className="mt-4">
          <div className="tinylabel">Text-message enrollment</div>
          <h1 className="text-3xl md:text-4xl font-black mt-1">
            Sign up for timesheet approval texts
          </h1>
          <p className="text-slate-600 mt-3">
            If you're a site supervisor working with Safety Onsite Solutions
            and would like to receive a text message when a contractor
            assigned to your project submits a weekly timesheet for your
            approval, enter your details below.
          </p>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-md p-6">
          <div className="tinylabel mb-2">What you'll receive</div>
          <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
            <li>A text message when a contractor submits a timesheet for your approval, containing a secure link to review and approve it.</li>
            <li>A reminder text if the timesheet remains unapproved after 24 or 48 hours.</li>
            <li><b>Typical frequency:</b> 1 – 2 messages per week per assigned project.</li>
            <li>Message and data rates may apply. You may reply <b>STOP</b> at any time to unsubscribe, or <b>HELP</b> for help.</li>
            <li>Enrollment is <b>voluntary</b> and not required in order to work with us or approve timesheets. You may approve timesheets by the emailed link alone if you prefer.</li>
          </ul>
        </div>

        <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-md p-6 space-y-4"
              data-testid="sms-signup-form">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Your name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="sms-signup-name"
              className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md"
              placeholder="Jane Smith"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Mobile phone number</label>
            <input
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="sms-signup-phone"
              className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md"
              placeholder="(740) 555-1234"
            />
            <div className="mt-1 text-xs text-slate-500">US mobile numbers only.</div>
          </div>

          <label className="flex items-start gap-3 p-3 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="sms-signup-consent"
              className="mt-1 h-4 w-4 accent-green-600 shrink-0"
            />
            <span className="text-sm text-slate-700">
              I agree to receive text messages from Safety Onsite Solutions
              regarding timesheet approvals for projects I supervise. Message
              frequency is typically 1 – 2 per week per project. Message and
              data rates may apply. I understand I can reply <b>STOP</b> at
              any time to cancel and <b>HELP</b> for help. I have read the{" "}
              <Link to="/privacy" className="text-green-700 hover:underline">Privacy Policy</Link>{" "}
              and{" "}
              <Link to="/terms" className="text-green-700 hover:underline">Terms &amp; Conditions</Link>.
            </span>
          </label>

          {err && (
            <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-md p-3">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={sending || !consent || !name || !phone}
            data-testid="sms-signup-submit"
            className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md text-sm disabled:opacity-60"
          >
            {sending ? "Signing up…" : "Sign me up for text notifications"}
          </button>
        </form>

        <div className="mt-6 bg-white border border-slate-200 rounded-md p-4 text-xs text-slate-600">
          <div className="font-bold text-slate-800 mb-1">Prefer email or phone instead?</div>
          If you'd rather skip texts, you can approve timesheets by the
          email link we send. Or contact us directly:
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-2"><Envelope size={14} className="text-slate-400" /> billing@safetyonsitesolutions.com</div>
            <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> (740) 550-9402</div>
          </div>
        </div>

        <div className="mt-6 text-xs text-slate-400 text-center">
          <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="hover:text-slate-700">Terms &amp; Conditions</Link>
        </div>
      </div>
    </div>
  );
}
